import {
  Divider,
  Hidden,
  IconButton,
  Modal,
  Paper,
  Theme,
  Tooltip,
  Typography,
  withStyles,
  WithStyles,
} from "@material-ui/core";
import { KeyboardArrowLeft } from "@material-ui/icons";
import Loader from "components/Loader";
import AuditLogTile from "components/request-dashboard/AuditLogTile";
import RequestStepper from "components/request-dashboard/RequestStepper";
import moment from "moment";
import React from "react";
import { connect, ConnectedProps } from "react-redux";
import { State } from "state";
import {
  fetchAnonymousAuditlog,
  hideRequestDetailsModal,
  showSearchEmailModal,
} from "state/request";
import { styles } from "styles/request_details";
import { getSteps, parseStringToJson } from "utils/helpers";

const muiStyles = (theme: Theme) => ({
  modalPaper: {
    position: "absolute" as const,
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxHeight: "90vh",
    display: "flex",
    flexDirection: "column" as const,
    borderRadius: 12,
    outline: "none",
    backgroundColor: "#fff",
    boxShadow: "0 10px 30px rgba(0,0,0,0.08)",

    [theme.breakpoints.up("sm")]: {
      width: "90%",
      minHeight: "50%",
    },
  },
});

const connecter = connect(
  (state: State) => ({
    showModal: state.requests.showRequestDetailsModal,
    anonymousRequestData: state.requests.anonymousRequestData,
    auditlogs: state.requests.auditlogs,
  }),
  { hideRequestDetailsModal, showSearchEmailModal, fetchAnonymousAuditlog }
);

type PropsFromRedux = ConnectedProps<typeof connecter>;
type Props = PropsFromRedux & WithStyles<typeof muiStyles>;

class RequestDetailsModal extends React.Component<Props> {
  componentDidUpdate(prevProps: Props) {
    if (!prevProps.showModal && this.props.showModal) {
      const id = this.props.anonymousRequestData.data.id;
      if (id) {
        this.props.fetchAnonymousAuditlog(id);
      }
    }
  }

  handleClose = () => {
    this.props.hideRequestDetailsModal();
    this.props.showSearchEmailModal();
  };

  render() {
    const {
      showModal,
      classes,
      anonymousRequestData: {
        data: {
          requestId,
          email,
          data,
          requestText,
          createdAt,
          currentStep,
          fromStatusCode,
          toStatusCode,
          currentApproverRole,
          workInProgressBy,
          requestType,
        },
      },
      auditlogs: { loading, data: auditlogsList },
    } = this.props;
    const details = parseStringToJson(data);
    const chainDescription = details?.data?.[details?.request_type]?.chain_description || "-";

    const logs = [
      { field: "requestId", label: "Request Id", value: requestId },
      { field: "requestText", label: "Request Type", value: requestText },
      { field: "requestDate", label: "Request for Chain", value: chainDescription },
      { field: "requestedBy", label: "Requested By", value: email },
      { field: "raisedOn", label: "Request Raised On", value: moment(createdAt).format("MM-DD-YYYY") },
    ];
    return (
      <Modal
        open={showModal}
        onClose={this.handleClose}
        closeAfterTransition
        disableBackdropClick
        BackdropProps={{ timeout: 300 }}
      >
        <Paper className={classes.modalPaper}>
          <div
            style={{
              borderBottom: `1px solid #e9eef2`,
              display: "flex",
              alignItems: "center",
              padding: "16px 32px",
              flexShrink: 0,
            }}
          >
            <IconButton onClick={this.handleClose} style={{ padding: "6px" }}>
              <KeyboardArrowLeft style={{ color: "#666" }} fontSize="inherit" />
            </IconButton>
            <div>
              <span style={styles.headerLabel}>Request ID : {requestId}</span>
              <p style={styles.description}>Description</p>
            </div>
          </div>
          <div style={{ display: "flex", flex: 1, overflow: "hidden", padding: "16px 32px" }}>
            <div
              style={{
                flex: "0 0 30%",
                minWidth: 250,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <Typography variant="body2" style={{ fontWeight: 700, marginBottom: 8 }}>
                Ticket Information
              </Typography>
              <table style={{ width: "100%", tableLayout: "fixed" }}>
                <tbody>
                  {logs.map(({ field, label, value }) => (
                    <tr key={field}>
                      <td
                        style={{
                          padding: "8px 16px 8px 0",
                          border: "none",
                          width: "40%",
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                        }}
                      >
                        <span style={styles.infoLabel}>{label}</span>
                      </td>
                      <td
                        style={{
                          padding: "8px 16px 8px 0",
                          border: "none",
                          textAlign: "right",
                          width: "60%",
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                        }}
                      >
                        <Tooltip title={value || ""} arrow>
                          <span
                            style={{
                              ...styles.infoValue,
                              display: "block",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              verticalAlign: "bottom",
                              cursor: value ? "pointer" : "default",
                            }}
                          >
                            {value || "-"}
                          </span>
                        </Tooltip>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Hidden smDown>
              <Divider orientation="vertical" flexItem style={{ margin: "0 16px" }} />
            </Hidden>

            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                backgroundColor: "#f7f8fa",
                padding: "16px",
                borderRadius: 8,
              }}
            >
              <RequestStepper
                steps={getSteps({
                  currentApproverRole: currentApproverRole ?? "",
                  requestType: requestType ?? "",
                  workInProgressBy: workInProgressBy ?? "",
                })}
                currentStep={currentStep}
                fromStatusCode={fromStatusCode}
                toStatusCode={toStatusCode}
              />
              <Typography variant="body2" style={{ fontWeight: 700 }}>
                Activity Logs
              </Typography>
              {loading ? (
                <Loader />
              ) : (
                <div
                  style={{
                    flex: 1,
                    overflowY: "auto",
                    marginTop: 16,
                    flexDirection: "column",
                    gap: "12px",
                    display: "flex",
                  }}
                >
                  {auditlogsList.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">
                      ** Your request is awaiting review **
                    </Typography>
                  ) : (
                    auditlogsList.map((log, index) => (
                      <AuditLogTile key={index} log={log} index={index} />
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        </Paper>
      </Modal>
    );
  }
}

export default connecter(withStyles(muiStyles)(RequestDetailsModal));
